"use client";
import { useQuery } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import classNames from "classnames";
import Link from "next/link";
import { ReactElement, useState } from "react";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import CustomizablePoster from "src/components/posterrow/CustomizablePoster";
import CheckboxSvg from "src/components/svg/CheckboxSvg";
import CloseSvg from "src/components/svg/CloseSvg";
import { useUserVoting } from "src/lib/client/useUserVoting";
import { IFilmomtale } from "src/lib/types/Filmomtale";
import styles from "./VotingForm.module.css";

//#region [Props]
type VotingFormProps = {
    className?: string;
    title: string;
    film: IFilmomtale;
    doClose: () => void;
    voteAppId: number;
    gdprId: number;
};
//#endregion

//#region [Component]
export default function VotingForm({ className, title, film, doClose, voteAppId, gdprId }: VotingFormProps) {
    const [email, setEmail] = useState<string>("")
    const [isAccepted, setAcceptance] = useState(false);

    const { queryKey, voteFunction } = useUserVoting(voteAppId, film.mainVersionEDI, email);

    const { isFetching, isError, refetch, isFetched, error, isSuccess } = useQuery({
        enabled: false,
        queryKey,
        queryFn: voteFunction,
        retry: false
    });

    return <div className={classNames(styles.VotingForm, className)}>
        <div className={styles.InnerForm}>
            <div className={styles.FormTitle}><span>{title}</span></div>

            <div className={styles.PosterContainer}>
                <CustomizablePoster film={film} width={200} className={styles.Poster} />
                <span>{film.title}</span>
            </div>
            <div className={styles.InputContainer}>
                <input type="email" className={styles.InputEmail} placeholder="Fyll inn din e-postadresse" value={email} onChange={(ev) => setEmail(ev.target.value?.trim() ?? "")} />
                <button className={styles.VotingButton} disabled={email === "" || !isAccepted || isSuccess} onClick={() => refetch()}>Stem</button>

            </div>
            <div className={styles.StatusContainer}>
                {isFetching && <LoadingIndicator position="InlineCenter" />}
                {isFetched && !isError && <p>Vi har nå sendt deg en e-post. Du må bekrefte e-postadressen din for at stemmen skal telles. </p>}
                {isError && <VotingError error={error} />}
            </div>
            <div className={styles.GDPRContainer}>
                <p><label><CheckboxSvg isChecked={isAccepted} /><input type="checkbox" checked={isAccepted} onChange={(ev) => setAcceptance(ev.target.checked)} /> Godta personvernerklæringen</label></p>
                <p>E-postadressen din vil lagres i forbindelse med avstemmingen. Les mer i vår <Link href={`https://forms.filmweb.no/form/displayform?id=${gdprId}`} target="_blank">personvernerklæring.</Link></p>

            </div>

            <button className={styles.CloseButton} onClick={doClose}><CloseSvg /></button>

        </div>

    </div>;
}
//#endregion

function VotingError({ error }: { error?: Error }) {
    const errorMsg = (error as AxiosError)?.response?.data;

    let elem: ReactElement | null = null;

    switch (errorMsg) {
        case "Too many votes":
            elem = <p className={styles.Error}>Du har brukt opp dine stemmer for denne gang.</p>;
            break;
        case "Voting closed":
            elem = <p className={styles.Error}>Stemmegivningen er avsluttet!</p>;
            break;
        default:
            elem = <p className={styles.Error}>Det oppstod en feil. Vennligst prøv igjen.</p>
    }

    return elem;
}